import React, { useState } from "react";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import { useFormik } from "formik";
import * as yup from "yup";
import UserPool from "../../UserPool";
import fetchClient from "../../api/fetchClient";
import message from "../../helper/message";
import NewPassword from "./NewPassword";
import LoginBranding from "../../components/login/LoginBranding";
import LoginForm from "../../components/login/LoginForm";
import ForgotPasswordForm from "../../components/login/ForgotPasswordForm";
import MfaForm from "../../components/login/MfaForm";
import MfaSetup from "../../components/login/MfaSetup";
import {
	LoginContainer,
	RightPanel,
	LoginContent,
	ContainerLogo,
	SeidorLogo
} from "../../components/login/styles";

const permissionsApiPath = process.env.REACT_APP_API_PATH;
const buildPermissionsUrl = (email) =>
	permissionsApiPath
		? `${permissionsApiPath}/getPermissaoUser/${email}`
		: `/getPermissaoUser/${email}`;

const validationSchema = yup.object({
	email: yup
		.string("Informe o login")
		.email("Email formato incorreto")
		.required("Login é obrigatório"),
	password: yup.string("Informe a senha").required("Senha é obrigatório"),
});

function Login({ setUser }) {
	const [userCognito, setUserCognito] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [forgotPassword, setForgotPassword] = useState(false);
	const [verificationCode, setVerificationCode] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [code, setCode] = useState("");
	const [newPasswordReset, setNewPasswordReset] = useState("");
	const [mfaChallenge, setMfaChallenge] = useState(null);
	const [mfaSetup, setMfaSetup] = useState(null);

	const handleForgotPassword = () => {
		if (!resetEmail) {
			message.error("Por favor, informe o e-mail");
			return;
		}

		const user = new CognitoUser({
			Username: resetEmail,
			Pool: UserPool,
		});

		setIsLoading(true);
		user.forgotPassword({
			onSuccess: (data) => {
				console.log("CodeDeliveryData from forgotPassword: " + data);
				message.sucess("Código de verificação enviado para o e-mail");
				setVerificationCode(true);
				setIsLoading(false);
			},
			onFailure: (err) => {
				console.log(err);
				message.error(err.message);
				setIsLoading(false);
			},
		});
	};

	const handleResetPassword = () => {
		if (!code || !newPasswordReset) {
			message.error("Por favor, preencha todos os campos");
			return;
		}

		const user = new CognitoUser({
			Username: resetEmail,
			Pool: UserPool,
		});

		setIsLoading(true);
		user.confirmPassword(code, newPasswordReset, {
			onSuccess: () => {
				message.sucess("Senha alterada com sucesso!");
				setForgotPassword(false);
				setVerificationCode(false);
				setResetEmail("");
				setCode("");
				setNewPasswordReset("");
				setIsLoading(false);
			},
			onFailure: (err) => {
				console.log(err);
				message.error(err.message);
				setIsLoading(false);
			},
		});
	};

	const handleBackToLogin = () => {
		setForgotPassword(false);
		setVerificationCode(false);
		setResetEmail("");
		setCode("");
		setNewPasswordReset("");
		setMfaChallenge(null);
		setMfaSetup(null);
	};

	const buildUserFromCognito = (email, data) => {
		const accessToken = data.getAccessToken().getJwtToken();
		const idToken = data.getIdToken();
		const idTokenPayload = idToken ? idToken.payload : {};

		return {
			email: email,
			token: accessToken,
			nickname: idTokenPayload.nickname || idTokenPayload.name || "",
			username: data.accessToken.payload.username,
			permission: idTokenPayload["custom:permissions"]
				? idTokenPayload["custom:permissions"].split("|")
				: [],
		};
	};

	const finishAuth = (email, data, onFinally) => {
		const user = buildUserFromCognito(email, data);
		console.log("🔍 [DEBUG] User construído do Cognito:", user);
		console.log("🔍 [DEBUG] Permissões do Cognito:", user.permission);
		localStorage.setItem("token", user.token);

		const permissionsUrl = buildPermissionsUrl(user.email);
		console.log("🔍 [DEBUG] URL de permissões:", permissionsUrl);

		fetchClient
			.get(permissionsUrl)
			.then((permission) => {
				console.log("✅ [DEBUG] Resposta da API de permissões:", permission.data);
				user.logs = permission.data ? permission.data.logsIds : [];

				localStorage.setItem("user", JSON.stringify(user));
				console.log("✅ [DEBUG] Usuário salvo no localStorage:", user);
				setUser(user);
				setIsLoading(false);
			})
			.catch((error) => {
				console.error("❌ [DEBUG] Erro ao buscar permissões da API, usando permissões do Cognito");
				console.error("❌ [DEBUG] Detalhes do erro:", error.response);

				// Mesmo com erro da API, salva o usuário com as permissões do Cognito
				user.logs = [];
				localStorage.setItem("user", JSON.stringify(user));
				console.log("✅ [DEBUG] Usuário salvo no localStorage (fallback):", user);
				setUser(user);
				setIsLoading(false);
			})
			.finally(() => {
				if (onFinally) onFinally();
			});
	};

	const completeNewPassword = ({ newPassword, nickname }, setLoading) => {
		const { email, user, userAttr } = userCognito;
		userAttr.nickname = nickname;
		user.completeNewPasswordChallenge(newPassword, userAttr, {
			onSuccess: (data) => {
				console.log("completeNewPasswordChallenge onSuccess", data);
				message.sucess("Senha alterada com sucesso");
				// Limpa o estado de primeiro login
				setUserCognito(null);

				// Verifica se há challengeName no retorno (indica que precisa de MFA)
				if (data.challengeName === "SOFTWARE_TOKEN_MFA" || data.challengeName === "SMS_MFA") {
					console.log("MFA necessário após troca de senha:", data.challengeName);
					setLoading(false);
					setIsLoading(false);
					setMfaChallenge({
						challengeName: data.challengeName,
						user,
						email,
					});
				} else if (data.challengeName === "MFA_SETUP") {
					console.log("Setup MFA necessário após troca de senha");
					user.associateSoftwareToken({
						associateSecretCode: (secretCode) => {
							const issuer = "SeidorPortal";
							const label = `${issuer}:${email}`;
							const otpauthUrl = `otpauth://totp/${encodeURIComponent(
								label
							)}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;

							setMfaSetup({
								user,
								email,
								secretCode,
								otpauthUrl,
							});
							setLoading(false);
							setIsLoading(false);
						},
						onFailure: (err) => {
							console.error("Erro ao configurar MFA:", err);
							message.error(err.message);
							setLoading(false);
							setIsLoading(false);
						},
					});
				} else {
					// Não tem MFA, finaliza autenticação normalmente
					console.log("Sem MFA, finalizando autenticação");
					setLoading(false);
					finishAuth(email, data);
				}
			},
			onFailure: (err) => {
				message.error(err.message);
				setLoading(false);
			},
			mfaRequired: (challengeName, challengeParameters) => {
				console.log("MFA requerido após nova senha:", challengeName);
				setUserCognito(null);
				setLoading(false);
				setIsLoading(false);
				setMfaChallenge({
					challengeName,
					user,
					email,
				});
			},
			mfaSetup: () => {
				console.log("MFA setup requerido após nova senha");
				setUserCognito(null);
				setLoading(false);
				user.associateSoftwareToken({
					associateSecretCode: (secretCode) => {
						const issuer = "SeidorPortal";
						const label = `${issuer}:${email}`;
						const otpauthUrl = `otpauth://totp/${encodeURIComponent(
							label
						)}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;

						setMfaSetup({
							user,
							email,
							secretCode,
							otpauthUrl,
						});
						setIsLoading(false);
					},
					onFailure: (err) => {
						message.error(err.message);
						setIsLoading(false);
					},
				});
			},
		});
	};

	const signIn = ({ email, password }) => {
		const user = new CognitoUser({
			Username: email,
			Pool: UserPool,
		});

		const autDetails = new AuthenticationDetails({
			Username: email,
			Password: password,
		});

		user.authenticateUser(autDetails, {
			onSuccess: (data) => {
				console.log("Login onSuccess:", data);
				finishAuth(email, data);
			},
			onFailure: (err) => {
				console.error("Login onFailure:", err);
				message.error(err.message);
				setIsLoading(false);
			},
			newPasswordRequired: (userAttributes, requiredAttributes) => {
				console.log("newPasswordRequired callback");
				delete userAttributes.email_verified;
				delete userAttributes.email;
				delete userAttributes.phone_number_verified;

				userAttributes.name = autDetails.username;

				setUserCognito({
					email: email,
					password: password,
					isFirstLogin: true,
					user: user,
					userAttr: userAttributes,
				});
			},
			mfaRequired: (challengeName, challengeParameters) => {
				console.log("mfaRequired callback disparado - MFA configurado e ativo", { challengeName, challengeParameters });
				// MFA está configurado corretamente, pede o código
				setIsLoading(false);
				setMfaChallenge({
					challengeName,
					user,
					email,
				});
			},
			totpRequired: (challengeName, challengeParameters) => {
				console.log("totpRequired callback - TOTP MFA ativo", { challengeName, challengeParameters });
				// TOTP MFA está configurado corretamente, pede o código
				setIsLoading(false);
				setMfaChallenge({
					challengeName: "SOFTWARE_TOKEN_MFA",
					user,
					email,
				});
			},
			selectMFAType: (challengeName, challengeParameters) => {
				// Se Cognito exigir escolha, prioriza SMS ou primeira opção disponível
				const mfaTypes =
					challengeParameters?.MFATypes?.split(",").map((t) => t.trim()) || [];
				const selectedType = mfaTypes.includes("SMS_MFA")
					? "SMS_MFA"
					: mfaTypes[0];

				if (!selectedType) {
					message.error("Nenhum método MFA disponível.");
					setIsLoading(false);
					return;
				}

				user.sendMFASelection(selectedType, {
					onSuccess: () => {
						setMfaChallenge({
							challengeName: selectedType,
							user,
							email,
						});
						setIsLoading(false);
					},
					onFailure: (err) => {
						message.error(err.message);
						setIsLoading(false);
					},
				});
			},
			mfaSetup: () => {
				user.associateSoftwareToken({
					associateSecretCode: (secretCode) => {
						const issuer = "SeidorPortal";
						const label = `${issuer}:${email}`;
						const otpauthUrl = `otpauth://totp/${encodeURIComponent(
							label
						)}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;

						setMfaSetup({
							user,
							email,
							secretCode,
							otpauthUrl,
						});
						setIsLoading(false);
					},
					onFailure: (err) => {
						message.error(err.message);
						setIsLoading(false);
					},
				});
			},
		});
	};

	const formik = useFormik({
		initialValues: { email: "", password: "" },
		validationSchema: validationSchema,
		enableReinitialize: true,
		onSubmit: (values) => {
			setIsLoading(true);
			signIn(values);
		},
		onFailure: () => {
			setIsLoading(false);
		},
	});

	const handleMfaSetupConfirm = (codeValue) => {
		setIsLoading(true);
		console.log("Verificando software token com código:", codeValue);

		mfaSetup.user.verifySoftwareToken(
			codeValue,
			`device-${mfaSetup.email}`,
			{
				onSuccess: (session) => {
					console.log("Token verificado com sucesso, configurando preferência MFA", session);

					mfaSetup.user.setUserMfaPreference(
						null,
						{ PreferredMfa: true, Enabled: true },
						(err) => {
							if (err) {
								console.error("Erro ao configurar preferência MFA:", err);
								message.error("Erro ao configurar MFA: " + err.message);
								setIsLoading(false);
								return;
							}

							console.log("Preferência MFA configurada, finalizando autenticação");

							// Após configurar MFA, precisa fazer login novamente para obter as credenciais completas
							// Vamos pedir para fazer login novamente
							message.sucess("MFA configurado com sucesso! Faça login novamente com seu código MFA.");
							setMfaSetup(null);
							setIsLoading(false);
							handleBackToLogin();
						}
					);
				},
				onFailure: (err) => {
					console.error("Erro ao verificar token:", err);
					if (err.code === "CodeMismatchException" || err.message.includes("Code mismatch")) {
						message.error("Código incorreto. Aguarde o próximo código do autenticador e tente novamente.");
					} else if (err.code === "EnableSoftwareTokenMFAException") {
						message.error("Erro ao ativar MFA. Tente novamente.");
					} else {
						message.error(err.message || "Erro ao validar código MFA");
					}
					setIsLoading(false);
				},
			}
		);
	};

	const handleMfaChallengeSubmit = (codeValue) => {
		setIsLoading(true);
		mfaChallenge.user.sendMFACode(
			codeValue,
			{
				onSuccess: (data) => {
					finishAuth(mfaChallenge.email, data, () => {
						// Limpa o estado do MFA challenge apenas após autenticação completa
						setMfaChallenge(null);
					});
				},
				onFailure: (err) => {
					console.error("Erro ao validar código MFA:", err);
					if (err.code === "CodeMismatchException" || err.message.includes("Code mismatch")) {
						message.error("Código incorreto. Aguarde o próximo código do autenticador e tente novamente.");
					} else if (err.code === "NotAuthorizedException") {
						message.error("Sessão expirada. Faça login novamente.");
						handleBackToLogin();
					} else {
						message.error(err.message || "Erro ao validar código MFA");
					}
					setIsLoading(false);
				},
			},
			mfaChallenge.challengeName
		);
	};

	const handleResetMfa = () => {
		if (!mfaChallenge) return;

		// Cancela e volta para tela de login
		handleBackToLogin();
		setTimeout(() => {
			message.warning("Para reconfigurar o MFA, entre em contato com o administrador do sistema para remover o MFA atual da sua conta no Cognito.");
		}, 300);
	};

	return (
		<LoginContainer>
			{mfaSetup ? (
				<MfaSetup
					secret={mfaSetup.secretCode}
					otpauthUrl={mfaSetup.otpauthUrl}
					isLoading={isLoading}
					onCancel={handleBackToLogin}
					onConfirm={handleMfaSetupConfirm}
				/>
			) : mfaChallenge ? (
				<MfaForm
					onSubmitCode={handleMfaChallengeSubmit}
					onCancel={handleBackToLogin}
					isLoading={isLoading}
					challengeName={mfaChallenge.challengeName}
					onResetMfa={handleResetMfa}
				/>
			) : !userCognito ? (
				<>
					<LoginBranding />
					<RightPanel>
						<LoginContent>
							{!forgotPassword ? (
								<>
									<LoginForm
										formik={formik}
										isLoading={isLoading}
										onForgotPassword={() => setForgotPassword(true)}
									/>
									<ContainerLogo>
										<SeidorLogo src="/image/logo-default.png" />
									</ContainerLogo>
								</>
							) : (
								<ForgotPasswordForm
									verificationCode={verificationCode}
									resetEmail={resetEmail}
									setResetEmail={setResetEmail}
									code={code}
									setCode={setCode}
									newPasswordReset={newPasswordReset}
									setNewPasswordReset={setNewPasswordReset}
									onSendCode={handleForgotPassword}
									onResetPassword={handleResetPassword}
									onBackToLogin={handleBackToLogin}
									isLoading={isLoading}
								/>
							)}
						</LoginContent>
					</RightPanel>
				</>
			) : (
				<NewPassword
					userCognito={userCognito}
					cancel={() => setUserCognito(null)}
					completeNewPassword={completeNewPassword}
				/>
			)}
		</LoginContainer>
	);
}

export default Login;
